import React, { useState, useMemo, useCallback } from 'react';

import { TemplatesProvider } from 'Templates';

import { Paper, Tabs, Tab, makeStyles } from '@gogame/components';
import Permissions, { useIsAllowed } from '@gogame/permissions';
import { Screen, withScreen, createScreenRoute } from '@gogame/screens';

import LastActivity from './LastActivity';
import PlayersInformation from './PlayersInformation';
import {
  TAB_LAST_ACTIVITY,
  TAB_PLAYERS_INFORMATION,
  isDevEnv,
} from './constants';
import { playersActivitiesLink, playersActivitiesParams } from './navigation';
import screenReducer from './screenReducer';

const id = 1073;

const useStyles = makeStyles((theme) => ({
  paper: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const permissionPlayersInfo = 50;

const PlayersActivities = withScreen({
  name: 'players-activities',
  searchParams: playersActivitiesParams,
  reducer: screenReducer,
})(() => {
  const classes = useStyles();
  const isAllowed = useIsAllowed();
  const [activeTabId, setActiveTabId] = useState(() => {
    const tabsWithPermissions = [
      ...(isDevEnv ? [{ tab: TAB_LAST_ACTIVITY }] : []),
      { tab: TAB_PLAYERS_INFORMATION, permission: permissionPlayersInfo },
    ];
    const allowedTabs = tabsWithPermissions.filter(
      ({ permission }) => permission === undefined || isAllowed(permission)
    );

    return allowedTabs?.[0]?.tab;
  });
  const handleTabChange = useCallback((_, value) => {
    setActiveTabId(value);
  }, []);

  const renderComponent = useMemo(() => {
    switch (activeTabId) {
      case TAB_LAST_ACTIVITY:
        return <LastActivity />;
      case TAB_PLAYERS_INFORMATION:
        return (
          <Permissions.Action id={permissionPlayersInfo}>
            <PlayersInformation />
          </Permissions.Action>
        );
      default:
        return null;
    }
  }, [activeTabId]);

  return (
    <React.Fragment>
      <Screen.Toolbar />
      <Screen.Body>
        <Paper className={classes.paper}>
          <Tabs
            value={activeTabId}
            onChange={handleTabChange}
            className={classes.tabs}
          >
            {isDevEnv ? (
              <Tab
                value={TAB_LAST_ACTIVITY}
                label={TAB_LAST_ACTIVITY}
                short={true}
              />
            ) : null}
            {isAllowed(permissionPlayersInfo) ? (
              <Tab
                value={TAB_PLAYERS_INFORMATION}
                label={TAB_PLAYERS_INFORMATION}
                short={true}
              />
            ) : null}
          </Tabs>
          {renderComponent}
        </Paper>
      </Screen.Body>
    </React.Fragment>
  );
});

export default createScreenRoute({
  title: 'Players Activities',
  link: playersActivitiesLink,
  screen: (
    <TemplatesProvider serverPageId={id}>
      <PlayersActivities />
    </TemplatesProvider>
  ),
  id: id,
});
